import React, { FC, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { Dispatch, iRootState, store } from '../../../store';

import UserMarkerGroup from './UserMarkerGroup';
import UserMarkerListItem from './UserMarkerListItem';

import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { IId } from '../../../models/types';

import apiConstants from '../../../constants/apiConstants';
import LocalStorageService from '../../../services/LocalStorageService';

const mapState = (state: iRootState) => ({
  map: state.infrastructureMap.map,
  users: state.infrastructureUsers.users,
  activeUser: state.infrastructureUsers.item,
  layers: state.infrastructureMap.layers,
  office: state.infrastructureOffices.item
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getUsers: d.infrastructureUsers.getUsers,
    resetUsers: d.infrastructureUsers.resetUsers,
    setUsersListInMap: d.infrastructureUsers.setUsersListInMap
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = connectedProps;

const UserMarkerList: FC<IProps> = ({
  users,
  map,
  layers,
  getUsers,
  setUsersListInMap,
  activeUser,
  resetUsers,
  office
}) => {
  const { id } = map;
  const { isUsers } = layers;
  const officeId = office?.id;
  const isAllPlans = id === 'all';
  const { REACT_APP_DEV_URL } = process.env;
  const connectionRef = useRef<HubConnection | null>(null);

  const { Connected, Disconnected } = HubConnectionState;

  const url = `${REACT_APP_DEV_URL || ''}${apiConstants.prefix}`;

  const createHubConnection = () => {
    const connection = new HubConnectionBuilder()
      .configureLogging(1)
      .withUrl(`${url}hubs/moves`, {
        accessTokenFactory: () => {
          const { accessToken } = LocalStorageService.getToken();
          return accessToken || '';
        }
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => {
          return 5000;
        }
      })
      .build();

    connectionRef.current = connection;
  };

  const signalRStop = async () => {
    const conn = connectionRef.current;
    if (conn) {
      try {
        conn.off('UpdateInfo');
        await conn.stop();
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.error(e);
      } finally {
        connectionRef.current = null;
      }
    }
  };

  const signalRSubscribe = async (mapId: IId, _officeId?: IId) => {
    const conn = connectionRef.current;
    if (!conn) return;

    if (conn.state === Disconnected) {
      await conn.start();
    }

    // Снимаем возможных предыдущих слушателей, чтобы не плодить дубликаты
    conn.off('UpdateInfo');

    if (isAllPlans && _officeId) {
      await conn.invoke('SubscribeToOffice', _officeId);
    } else {
      await conn.invoke('SubscribeToMap', mapId);
    }

    conn.onreconnected(() => {
      if (isAllPlans && _officeId) {
        conn.invoke('SubscribeToOffice', _officeId);
      } else {
        conn.invoke('SubscribeToMap', mapId);
      }
    });

    conn.on('UpdateInfo', payload => {
      // tslint:disable-next-line: no-console
      console.log(payload);
      // Для "Все планы" передаем officeId, для конкретного плана mapId
      getUsers({
        payload,
        mapId: isAllPlans ? _officeId : mapId
      });
    });
  };

  const signalRUnsubscribe = (mapId: IId, _officeId?: IId) => {
    const conn = connectionRef.current;
    if (conn && conn.state === Connected) {
      conn.off('UpdateInfo');
      if (isAllPlans && _officeId) {
        conn.invoke('UnsubscribeFromOffice', _officeId).catch(err => console.error(err));
      } else {
        conn.invoke('UnsubscribeFromMap', mapId).catch(err => console.error(err));
      }
    }
  };

  useEffect(() => {
    createHubConnection();
    return () => {
      signalRStop();
    };
  }, []);

  useEffect(() => {
    if (!id || !officeId || !connectionRef.current) {
      return;
    }
    // Для "Все планы" передаем объект с флагом, иначе mapId
    if (isAllPlans) {
      setUsersListInMap({ placeId: officeId, isAllPlans: true }).then(() => {
        signalRSubscribe(id, officeId);
      });
    } else {
      setUsersListInMap(id).then(() => {
        signalRSubscribe(id, officeId);
      });
    }
    return () => {
      signalRUnsubscribe(id, officeId);
    };
  }, [id, officeId, isAllPlans]);

  // Загружаем офис при восстановлении "Все планы" после обновления страницы
  useEffect(() => {
    if (isAllPlans && !officeId) {
      const savedOfficeId = localStorage.getItem('officeId');
      if (savedOfficeId) {
        // Диспатчим загрузку офиса
        store.dispatch.infrastructureOffices.getOffice(savedOfficeId);
      }
    }
  }, [isAllPlans, officeId]);
  return (
    <>
      <UserMarkerGroup>
        {isUsers &&
          users
            .filter(user => !(activeUser && activeUser.id === user.id))
            .map(user => <UserMarkerListItem key={JSON.stringify(user)} user={user} />)}
      </UserMarkerGroup>
      {activeUser && <UserMarkerListItem key={activeUser.id} user={activeUser} />}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(UserMarkerList);
